// IANA timezone picker.
//
// This was a free-text field validated only by trim(), so "Europe/Londn"
// saved happily and the report then fired at the wrong hour — or not at all.
// The list comes from the browser's own tz database, which is the same source
// Intl.DateTimeFormat validates against when the saved value is displayed, so
// a value that can be chosen here is a value that can be rendered later.
import PropTypes from 'prop-types';
import { Autocomplete, TextField } from '@mui/material';

export const DEFAULT_TIMEZONE = 'Europe/London';

// Intl.supportedValuesOf is unavailable on older engines. The fallback is
// deliberately short rather than a vendored tz database: it covers the zones
// a British dental group plausibly reports in, and the default is the one
// almost every practice wants.
const FALLBACK_ZONES = [
    'Europe/London',
    'Europe/Dublin',
    'Europe/Paris',
    'Europe/Madrid',
    'Europe/Lisbon',
    'Europe/Berlin',
    'Europe/Amsterdam',
    'Europe/Brussels',
    'Europe/Warsaw',
    'Europe/Bucharest',
    'UTC',
];

export function supportedTimezones() {
    try {
        const zones = Intl.supportedValuesOf?.('timeZone');
        if (Array.isArray(zones) && zones.length) return zones;
    } catch {
        // Fall through to the short list below.
    }
    return FALLBACK_ZONES;
}

// A saved schedule can carry a zone this browser does not list — an older
// alias, or a zone added since. Keep it selectable so opening the dialog
// cannot silently rewrite someone's saved value.
export const timezoneOptions = (current) => {
    const zones = supportedTimezones();
    if (current && !zones.includes(current)) return [current, ...zones];
    return zones;
};

export const isValidTimezone = (value) => {
    if (!value || typeof value !== 'string') return false;
    try {
        new Intl.DateTimeFormat('en-GB', { timeZone: value }).format(new Date());
        return true;
    } catch {
        return false;
    }
};

export default function TimezoneSelect({ value, onChange, disabled = false, label = 'Timezone', helperText }) {
    const invalid = Boolean(value) && !isValidTimezone(value);
    return (
        <Autocomplete
            options={timezoneOptions(value)}
            value={value || null}
            onChange={(event, next) => onChange(next || '')}
            disabled={disabled}
            disableClearable={!!value}
            autoHighlight
            fullWidth
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    required
                    error={invalid}
                    helperText={
                        invalid
                            ? 'This timezone is not recognised. Choose one from the list.'
                            : helperText || `Delivery times are in this zone. Default ${DEFAULT_TIMEZONE}.`
                    }
                />
            )}
        />
    );
}

TimezoneSelect.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    label: PropTypes.string,
    helperText: PropTypes.string,
};
